"use client";

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { isNativePlatform } from "@/lib/platform";
import { initializeNativeFeatures } from "@/lib/native-init";
import { setDeepLinkNavigator } from "@/lib/deep-link-handler";
import { WebRouter } from "@/routers/WebRouter";
import { MobileRouter } from "@/routers/MobileRouter";
import { DesignerProvider } from "@/contexts/DesignerContext";
import { trackPageView } from "./lib/analytics";
import { useVisitTracker } from "./hooks/useVisitTracker";
import { useUsername } from "./hooks/useUsername";
import { CookieConsent } from "./components/CookieConsent";
import { Footer } from "./components/Footer";
import { PolicyUpdateModal } from "./components/PolicyUpdateModal";
import { usePolicyCheck } from "./hooks/usePolicyCheck";
import { ScrollToTop } from "./components/ScrollToTop";
import { AppUpdateDialog } from "./components/mobile/AppUpdateDialog";
import { supabase } from "@/integrations/supabase/client";
import { recordPresence } from "@/lib/record-presence";
import { initCampaignAttribution } from "@/lib/campaign-attribution";

const queryClient = new QueryClient();

// Component to track page views
const PageViewTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;
    trackPageView(location.pathname + location.search, document.title);
  }, [location]);

  return null;
};

// Inner component that can access useLocation and useNavigate
const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isNative = isNativePlatform();
  const { needsAcceptance, policyType, newVersion, effectiveDate, markPolicyAccepted } = usePolicyCheck();
  useVisitTracker();
  useEffect(() => { initCampaignAttribution(); }, []);
  useUsername();

  // Set up deep link navigator for native platforms
  useEffect(() => {
    if (isNative) {
      setDeepLinkNavigator(navigate);
    }
  }, [isNative, navigate]);

  // Record server-side presence (location + last_seen) when session is ready
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (!session) return;
      if (event === "INITIAL_SESSION" || event === "SIGNED_IN") {
        recordPresence().catch(() => {});
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // Hide footer on payment-return page (shown in WebView from mobile)
  const hideFooter = isNative || location.pathname === "/payment-return";

  return (
    <>
      <ScrollToTop />
      <PageViewTracker />
      {isNative && <AppUpdateDialog />}
      {!isNative && (
        <>
          <CookieConsent />
          <PolicyUpdateModal
            open={needsAcceptance}
            onAccept={markPolicyAccepted}
            policyType={policyType || "privacy"}
            newVersion={newVersion}
            effectiveDate={effectiveDate}
          />
        </>
      )}
      <div className="flex flex-col min-h-screen">
        {isNative ? <MobileRouter /> : <WebRouter />}
        {!hideFooter && <Footer />}
      </div>
    </>
  );
};

const App = () => {
  useEffect(() => {
    initializeNativeFeatures();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <DesignerProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </TooltipProvider>
      </DesignerProvider>
    </QueryClientProvider>
  );
};

export default App;
